import { Area, AreaChart, CartesianGrid, ReferenceDot, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatMoney } from "@/utils/format";
import type { TreasuryForecast } from "@/services/treasuryService";

type Day = TreasuryForecast["days"][number];

const compact = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} M`;
  if (abs >= 1_000) return `${Math.round(n / 1_000).toLocaleString("fr-FR")} k`;
  return String(Math.round(n));
};

const shortDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const longDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

function DayTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: Day }> }) {
  const day = active ? payload?.[0]?.payload : undefined;
  if (!day) return null;
  return (
    <div className="min-w-[12rem] rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-xs shadow-raised">
      <p className="font-semibold capitalize text-foreground-950">{longDate(day.date)}</p>
      <p className={`mt-1 text-sm font-bold tabular-nums ${day.balance < 0 ? "text-accent-700" : "text-primary-800"}`}>
        {formatMoney(day.balance)}
      </p>
      {(day.inflow > 0 || day.outflow > 0) && (
        <div className="mt-1.5 space-y-0.5 border-t border-background-200 pt-1.5 tabular-nums">
          {day.inflow > 0 && <p className="text-foreground-600">Entrées prévues : <span className="font-medium text-primary-700">+{formatMoney(day.inflow)}</span></p>}
          {day.outflow > 0 && <p className="text-foreground-600">Sorties prévues : <span className="font-medium text-accent-700">−{formatMoney(day.outflow)}</span></p>}
        </div>
      )}
    </div>
  );
}

/** Courbe du solde prévu jour par jour : ligne du zéro, point le plus bas, détail du jour au survol. */
export default function BalanceChart({ forecast }: { forecast: TreasuryForecast }) {
  const days = forecast.days;
  if (days.length < 2) return null;
  const negative = !!forecast.firstNegativeDate;
  const color = negative ? "var(--accent-500)" : "var(--primary-500)";
  const low = days.find((d) => d.date === forecast.minDate);

  return (
    <div className="h-72 w-full" role="img" aria-label="Solde prévu jour par jour">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={days} margin={{ top: 12, right: 12, left: 4, bottom: 0 }}>
          <defs>
            <linearGradient id="soldePrevu" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={`oklch(${color})`} stopOpacity={0.35} />
              <stop offset="100%" stopColor={`oklch(${color})`} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={shortDate}
            minTickGap={28}
            tick={{ fontSize: 11, fill: "oklch(var(--foreground-500))" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickFormatter={compact}
            width={56}
            tick={{ fontSize: 11, fill: "oklch(var(--foreground-500))" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<DayTooltip />} cursor={{ stroke: "oklch(var(--foreground-300))", strokeDasharray: "4 3" }} />
          <ReferenceLine y={0} stroke="oklch(var(--foreground-400))" strokeDasharray="4 3" />
          <Area
            type="monotone"
            dataKey="balance"
            stroke={`oklch(${color})`}
            strokeWidth={2.5}
            fill="url(#soldePrevu)"
            activeDot={{ r: 5, strokeWidth: 2, stroke: "oklch(var(--background-50))" }}
            animationDuration={700}
          />
          {low && (
            <ReferenceDot
              x={low.date}
              y={low.balance}
              r={5}
              fill="oklch(var(--accent-600))"
              stroke="oklch(var(--background-50))"
              strokeWidth={2}
              label={{ value: `Plus bas : ${compact(low.balance)}`, position: "top", fontSize: 11, fill: "oklch(var(--accent-700))" }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
